import { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Send, 
  RefreshCw, 
  Loader2,
  Building2,
  Mail,
  User,
  Shield,
  XCircle,
  CheckCircle,
  Clock,
  AlertCircle,
  MoreHorizontal,
  Eye,
  Link2,
  FileText,
  Download,
  Upload,
  Leaf,
  ChevronsUpDown,
  Check,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { vendorAdminApi, platformsApi, tierAdminApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const TIERS = [
  { 
    value: 'claimed', 
    label: 'Claimed', 
    description: 'Basic fields only, all edits need approval',
    color: 'bg-muted text-muted-foreground',
    features: ['Basic profile editing', '3 edits/day limit', 'Admin approval required']
  },
  { 
    value: 'verified', 
    label: 'Verified', 
    description: 'Public badge, more fields, links auto-publish',
    color: 'bg-blue-100 text-blue-700',
    features: ['Public "Verified Partner" badge', '10 edits/day', 'Links auto-publish', 'View analytics']
  },
  { 
    value: 'enterprise', 
    label: 'Enterprise', 
    description: 'Fast lane publishing, full access',
    color: 'bg-amber-100 text-amber-700',
    features: ['Public "Enterprise Partner" badge', '50 edits/day', 'Safe fields auto-publish', 'Full analytics', 'View comparisons']
  },
];

const STATUSES = [
  { value: 'draft', label: 'Draft', icon: FileText, className: 'text-muted-foreground' },
  { value: 'invited', label: 'Invited', icon: Mail, className: 'text-blue-600' },
  { value: 'active', label: 'Active', icon: CheckCircle, className: 'text-success' },
  { value: 'suspended', label: 'Suspended', icon: XCircle, className: 'text-destructive' },
];

const INVITE_STATUSES = {
  pending: { label: 'Not Sent', className: 'bg-muted text-muted-foreground' },
  sent: { label: 'Sent', className: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Accepted', className: 'bg-green-100 text-green-700' },
  expired: { label: 'Expired', className: 'bg-amber-100 text-amber-700' },
  revoked: { label: 'Revoked', className: 'bg-red-100 text-red-700' },
};

const getStatusConfig = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];
const getTierLabel = (tier) => TIERS.find(t => t.value === tier)?.label || tier;

export const VendorManager = () => {
  const [vendors, setVendors] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc'); // 'name-asc', 'name-desc', 'date-asc', 'date-desc', 'status'
  const [editingVendor, setEditingVendor] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [unlinkedCount, setUnlinkedCount] = useState(0);
  const [platformComboboxOpen, setPlatformComboboxOpen] = useState(false);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    linked_platform_ids: [],
    tier: 'claimed',
    admin_notes: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vendorsData, platformsData, unlinkedData] = await Promise.all([
        vendorAdminApi.getAll(),
        platformsApi.getAll(),
        vendorAdminApi.getUnlinkedPlatforms()
      ]);
      setVendors(vendorsData);
      setPlatforms(platformsData);
      setUnlinkedCount(unlinkedData.unlinked_count || 0);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast({ title: 'Error', description: 'Failed to load vendors', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredVendors = vendors
    .filter(v =>
      v.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.email?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return (a.company_name || '').localeCompare(b.company_name || '');
        case 'name-desc':
          return (b.company_name || '').localeCompare(a.company_name || '');
        case 'date-asc':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'date-desc':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'status':
          const statusOrder = { active: 0, invited: 1, draft: 2, suspended: 3 };
          return (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
        case 'tier':
          const tierOrder = { enterprise: 0, verified: 1, claimed: 2 };
          return (tierOrder[a.tier] || 3) - (tierOrder[b.tier] || 3);
        default:
          return 0;
      }
    });

  const handleAddNew = () => {
    setFormData({
      company_name: '',
      contact_name: '',
      email: '',
      linked_platform_ids: [],
      tier: 'claimed',
      admin_notes: '',
    });
    setEditingVendor({ id: 'new' });
    setIsAddingNew(true);
  };

  const handleEdit = (vendor) => {
    setFormData({
      company_name: vendor.company_name || '',
      contact_name: vendor.contact_name || '',
      email: vendor.email || '',
      linked_platform_ids: vendor.linked_platform_ids || [],
      tier: vendor.tier || 'claimed',
      admin_notes: vendor.admin_notes || '',
      status: vendor.status || 'draft',
    });
    setEditingVendor(vendor);
    setIsAddingNew(false);
  };

  const handleSave = async () => {
    if (!formData.company_name || !formData.email) {
      toast({ title: 'Validation Error', description: 'Company name and email are required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (isAddingNew) {
        await vendorAdminApi.create(formData);
        toast({ title: 'Vendor created', description: `${formData.company_name} has been added` });
      } else {
        await vendorAdminApi.update(editingVendor.id, formData);
        toast({ title: 'Vendor updated', description: `${formData.company_name} has been updated` });
      }
      await fetchData();
      setEditingVendor(null);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await vendorAdminApi.delete(deleteConfirm.id);
      toast({ title: 'Vendor deleted', description: `${deleteConfirm.company_name} has been removed` });
      await fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleSendInvite = async (vendor) => {
    setSendingInvite(vendor.id);
    try {
      const result = await vendorAdminApi.sendInvite(vendor.id);
      if (result.email_sent) {
        toast({ title: 'Invite sent', description: `Invitation email sent to ${vendor.email}` });
      } else {
        toast({ title: 'Invite created', description: 'Invite token created but email sending may have failed', variant: 'warning' });
      }
      await fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSendingInvite(null);
    }
  };

  const handleRevokeInvite = async (vendor) => {
    try {
      await vendorAdminApi.revokeInvite(vendor.id);
      toast({ title: 'Invite revoked', description: `Invitation for ${vendor.company_name} has been revoked` });
      await fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleStatusChange = async (vendor, newStatus) => {
    try {
      await vendorAdminApi.update(vendor.id, { status: newStatus });
      toast({ title: 'Status updated', description: `${vendor.company_name} is now ${newStatus}` });
      await fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const getPlatformName = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    return platform?.name || platformId;
  };

  const handleQuickTierUpgrade = async (vendor, newTier) => {
    const tierNames = { verified: 'Verified Partner', enterprise: 'Enterprise Partner' };
    const paymentRef = prompt(`Enter payment reference for ${tierNames[newTier]} upgrade (optional):`);
    
    try {
      await vendorAdminApi.upgradeTier(vendor.id, newTier, paymentRef || null);
      toast({ 
        title: 'Tier Upgraded', 
        description: `${vendor.company_name} is now a ${tierNames[newTier]}` 
      });
      await fetchData();
    } catch (err) {
      toast({ title: 'Upgrade Failed', description: err.message, variant: 'destructive' });
    }
  };

  const handleBulkImport = async () => {
    setBulkImporting(true);
    try {
      const result = await vendorAdminApi.bulkCreateFromPlatforms();
      setImportResults(result);
      toast({ 
        title: 'Import Complete', 
        description: `Created ${result.created} vendor entries. ${result.skipped} already existed.` 
      });
      await fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setBulkImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Vendor Management</h2>
          <p className="text-sm text-muted-foreground">Create and manage vendor accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {unlinkedCount > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleBulkImport}
              disabled={bulkImporting}
              className="text-primary border-primary/30 hover:bg-primary/10"
            >
              {bulkImporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Import All Platforms ({unlinkedCount})
            </Button>
          )}
          <Button size="sm" onClick={handleAddNew}>
            <Plus className="w-4 h-4 mr-2" />
            Add Vendor
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-semibold">{vendors.length}</p>
            <p className="text-xs text-muted-foreground">Total Vendors</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-semibold text-success">{vendors.filter(v => v.status === 'active').length}</p>
            <p className="text-xs text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-semibold text-blue-600">{vendors.filter(v => v.status === 'invited').length}</p>
            <p className="text-xs text-muted-foreground">Invited</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-semibold text-muted-foreground">{vendors.filter(v => v.status === 'draft').length}</p>
            <p className="text-xs text-muted-foreground">Draft</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Sort */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sort by..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name-asc">
              <span className="flex items-center gap-2">
                <ArrowUp className="w-3 h-3" /> Name (A-Z)
              </span>
            </SelectItem>
            <SelectItem value="name-desc">
              <span className="flex items-center gap-2">
                <ArrowDown className="w-3 h-3" /> Name (Z-A)
              </span>
            </SelectItem>
            <SelectItem value="date-desc">
              <span className="flex items-center gap-2">
                <Clock className="w-3 h-3" /> Newest First
              </span>
            </SelectItem>
            <SelectItem value="date-asc">
              <span className="flex items-center gap-2">
                <Clock className="w-3 h-3" /> Oldest First
              </span>
            </SelectItem>
            <SelectItem value="status">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3" /> By Status
              </span>
            </SelectItem>
            <SelectItem value="tier">
              <span className="flex items-center gap-2">
                <Shield className="w-3 h-3" /> By Tier
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Vendor List */}
      {filteredVendors.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No vendors found</p>
            <Button variant="outline" className="mt-4" onClick={handleAddNew}>
              <Plus className="w-4 h-4 mr-2" />
              Add your first vendor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredVendors.map(vendor => {
            const statusConfig = getStatusConfig(vendor.status);
            const StatusIcon = statusConfig.icon;
            const inviteStatus = INVITE_STATUSES[vendor.invite_status] || INVITE_STATUSES.pending;
            const tierConfig = TIERS.find(t => t.value === vendor.tier) || TIERS[0];
            
            return (
              <Card key={vendor.id} className={cn(vendor.status === 'suspended' && 'opacity-60')}>
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    {/* Vendor Info */}
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="p-2 bg-muted rounded-lg">
                        <Building2 className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-foreground truncate">{vendor.company_name}</h3>
                          <Badge variant="outline" className={cn('text-xs', tierConfig.color)}>
                            {tierConfig.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {vendor.contact_name || 'No contact'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {vendor.email || 'No email'}
                          </span>
                        </div>
                        {vendor.linked_platform_ids?.length > 0 && (
                          <div className="flex items-center gap-1 mt-2 flex-wrap">
                            <Link2 className="w-3 h-3 text-muted-foreground" />
                            {vendor.linked_platform_ids.map(id => (
                              <Badge key={id} variant="secondary" className="text-xs">
                                {getPlatformName(id)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex items-center gap-3 sm:flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <Badge className={cn('text-xs', inviteStatus.className)}>
                          {inviteStatus.label}
                        </Badge>
                        <div className={cn('flex items-center gap-1 text-sm', statusConfig.className)}>
                          <StatusIcon className="w-4 h-4" />
                          {statusConfig.label}
                        </div>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(vendor)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          
                          <DropdownMenuSeparator />
                          
                          {(vendor.status === 'draft' || vendor.status === 'invited') && (
                            <DropdownMenuItem 
                              onClick={() => handleSendInvite(vendor)}
                              disabled={sendingInvite === vendor.id}
                            >
                              {sendingInvite === vendor.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4 mr-2" />
                              )}
                              {vendor.invite_status === 'sent' ? 'Resend Invite' : 'Send Invite'}
                            </DropdownMenuItem>
                          )}
                          
                          {vendor.invite_status === 'sent' && (
                            <DropdownMenuItem onClick={() => handleRevokeInvite(vendor)}>
                              <XCircle className="w-4 h-4 mr-2" />
                              Revoke Invite
                            </DropdownMenuItem>
                          )}
                          
                          <DropdownMenuSeparator />
                          
                          {vendor.status === 'active' && (
                            <DropdownMenuItem 
                              onClick={() => handleStatusChange(vendor, 'suspended')}
                              className="text-destructive"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Suspend
                            </DropdownMenuItem>
                          )}
                          
                          {vendor.status === 'suspended' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(vendor, 'active')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Reactivate
                            </DropdownMenuItem>
                          )}
                          
                          {/* Quick Tier Upgrade */}
                          {vendor.status === 'active' && vendor.tier !== 'enterprise' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-blue-600 font-medium"
                                onClick={() => handleQuickTierUpgrade(vendor, 'verified')}
                                disabled={vendor.tier === 'verified'}
                              >
                                <Shield className="w-4 h-4 mr-2" />
                                {vendor.tier === 'verified' ? '✓ Verified Partner' : 'Upgrade to Verified'}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-amber-600 font-medium"
                                onClick={() => handleQuickTierUpgrade(vendor, 'enterprise')}
                              >
                                <Shield className="w-4 h-4 mr-2" />
                                Upgrade to Enterprise
                              </DropdownMenuItem>
                            </>
                          )}
                          
                          <DropdownMenuSeparator />
                          
                          <DropdownMenuItem 
                            onClick={() => setDeleteConfirm(vendor)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={!!editingVendor} onOpenChange={() => setEditingVendor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isAddingNew ? 'Add New Vendor' : 'Edit Vendor'}</DialogTitle>
            <DialogDescription>
              {isAddingNew ? 'Create a new vendor account' : 'Update vendor information'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Acme AI Inc."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@acme.ai"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Vendor Tier</Label>
              <Select
                value={formData.tier}
                onValueChange={(value) => setFormData(prev => ({ ...prev, tier: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map(tier => (
                    <SelectItem key={tier.value} value={tier.value}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={cn('text-xs', tier.color)}>
                          {tier.label}
                        </Badge>
                        <span className="text-muted-foreground text-xs">{tier.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Show tier permissions */}
              {formData.tier && (
                <div className="mt-2 p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs font-medium mb-2">Tier Permissions:</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    {TIERS.find(t => t.value === formData.tier)?.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-success" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Linked Platforms</Label>
              <Popover open={platformComboboxOpen} onOpenChange={setPlatformComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={platformComboboxOpen}
                    className="w-full justify-between font-normal"
                  >
                    {formData.linked_platform_ids[0]
                      ? platforms.find(p => p.id === formData.linked_platform_ids[0])?.name
                      : "Search and select a platform..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Type to search platforms..." />
                    <CommandList>
                      <CommandEmpty>No platform found.</CommandEmpty>
                      <CommandGroup>
                        {platforms
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(platform => (
                          <CommandItem
                            key={platform.id}
                            value={platform.name}
                            onSelect={() => {
                              setFormData(prev => ({
                                ...prev,
                                linked_platform_ids: [platform.id]
                              }));
                              setPlatformComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                formData.linked_platform_ids[0] === platform.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              }`}
                            />
                            {platform.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Vendor can only edit platforms they are linked to
              </p>
            </div>
            
            {!isAddingNew && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(status => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Admin Notes</Label>
              <Textarea
                value={formData.admin_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, admin_notes: e.target.value }))}
                placeholder="Internal notes (invoice status, renewal dates, etc.)"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditingVendor(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isAddingNew ? 'Create Vendor' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteConfirm?.company_name}</strong>?
              This will also delete all their pending edits. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Results Dialog */}
      <Dialog open={!!importResults} onOpenChange={() => setImportResults(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              Vendor Import Complete
            </DialogTitle>
            <DialogDescription>
              Created {importResults?.created || 0} new vendor entries. {importResults?.skipped || 0} platforms already had vendors.
            </DialogDescription>
          </DialogHeader>
          
          {importResults?.vendors?.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-sm mb-3">Created Vendors:</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {importResults.vendors.map((v, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium">{v.company_name}</p>
                      <p className="text-xs text-muted-foreground">Platform: {v.platform_name}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">Draft</Badge>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Next step: Edit each vendor to add contact name and email, then send invites.
              </p>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button onClick={() => setImportResults(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
